import { useEffect, useRef, useState } from 'react';
import { AudioControls } from './AudioControls';
import './AudioPlayer.css';
import { Backdrop } from './Backdrop';

export type ITrack = {
  title: string;
  artist: string;
  audioSrc: string;
  image: string;
};

type AudioPlayerProps = {
  tracks: ITrack[];
};

export const AudioPlayer = ({ tracks }: AudioPlayerProps) => {
  // #region state

  const [trackIndex, setTrackIndex] = useState(0);
  const [trackProgress, setTrackProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // #endregion

  const { title, artist, image, audioSrc } = tracks[trackIndex];

  // #region refs

  const audioRef = useRef(new Audio(audioSrc));
  const intervalRef = useRef<number>();
  const isReady = useRef(false);

  // #endregion

  const { duration } = audioRef.current;

  // #region event handler

  const toPrevTrack = () => {
    const prev = trackIndex - 1;
    if (prev < 0) {
      setTrackIndex(tracks.length - 1);
    } else {
      setTrackIndex(prev);
    }
  };

  const toNextTrack = () => {
    if (trackIndex < tracks.length - 1) {
      setTrackIndex(trackIndex + 1);
    } else {
      setTrackIndex(0);
    }
  };

  const startTimer = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = window.setInterval(() => {
      if (audioRef.current.ended) {
        toNextTrack();
      } else {
        setTrackProgress(audioRef.current.currentTime);
      }
    }, 1000);
  };

  const onScrub = (value: string) => {
    // Clear any timers already running
    clearInterval(intervalRef.current);
    audioRef.current.currentTime = Number(value);
    setTrackProgress(audioRef.current.currentTime);
  };

  const onScrubEnd = () => {
    // If not already playing, start
    if (!isPlaying) {
      setIsPlaying(true);
    }
    startTimer();
  };

  // #endregion

  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play();
      startTimer();
    } else {
      clearInterval(intervalRef.current);
      audioRef.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    audioRef.current?.pause();
    audioRef.current = new Audio(audioSrc);
    setTrackProgress(audioRef.current.currentTime);

    if (isReady.current) {
      audioRef.current.play();
      setIsPlaying(true);
      startTimer();
    } else {
      // set the isReady ref as true for the next pass
      isReady.current = true;
    }
  }, [trackIndex, audioSrc]);

  const currentPercentage = duration
    ? `${(trackProgress / duration) * 100}%`
    : '0%';
  const trackStyling = `
  -webkit-gradient(linear, 0% 0%, 100% 0%, color-stop(${currentPercentage}, #fff), color-stop(${currentPercentage}, #777))
`;

  return (
    <div className="audio-player">
      <div className="track-info">
        <h2 className="title">{title}</h2>
        <h3 className="artist">{artist}</h3>
        <AudioControls
          isPlaying={isPlaying}
          onPrevClick={toPrevTrack}
          onNextClick={toNextTrack}
          onPlayPauseClick={setIsPlaying}
        />
        <input
          type="range"
          value={trackProgress}
          step="1"
          min="0"
          max={duration ? duration : `${duration}`}
          className="progress"
          onChange={(e) => onScrub(e.target.value)}
          onMouseUp={onScrubEnd}
          onKeyUp={onScrubEnd}
          style={{ background: trackStyling }}
        />
      </div>
      <Backdrop
        image={image}
        trackIndex={trackIndex}
        isPlaying={isPlaying}
      ></Backdrop>
    </div>
  );
};
